import type { StoreApi } from "zustand";

import type { FieldId, FieldValueType } from "../../../types";
import type { Attribute, FieldValue } from "../../../types";
import type { CoreActions, FormStore, SliceSetFunction } from "../../types";

/**
 * フォームの基本機能に関するアクション
 * - フォーム登録
 * - 値の設定/取得
 * - タッチ状態の管理
 * - 読み取り専用モードの設定
 */
export const createCoreActions = (
  set: SliceSetFunction,
  get: () => FormStore,
  _store: StoreApi<FormStore>,
): CoreActions => ({
  /**
   * フォーム定義を登録する
   */
  register: (formDefinition) => {
    set({ form: formDefinition, initialized: true }, false, "core/register");
  },

  /**
   * 複数フィールドの値を一括設定
   */
  setValues: (values, validate = true) => {
    set({ values: [...values] }, false, "core/setValues");
    if (validate) get().validateAll();
  },

  /**
   * 単一フィールドの値を設定
   */
  setValue: (fieldId, value) => {
    const state = get();
    const attribute = state.getAttribute(fieldId);
    const newValues = computeNewValues(state.values, fieldId, value, attribute);
    set({ values: newValues }, false, "core/setValue");
    state.removeFieldError(fieldId);
    state.setTouched(fieldId, true);
  },

  /**
   * フィールドの値を削除
   */
  removeValue: (fieldId) => {
    const state = get();
    set(
      { values: state.values.filter((v) => v.fieldId !== fieldId) },
      false,
      "core/removeValue",
    );
    state.removeFieldError(fieldId);
  },

  /**
   * フィールドのタッチ状態を設定
   */
  setTouched: (fieldId, touched) => {
    set(
      { touched: { ...get().touched, [fieldId]: touched } },
      false,
      "core/setTouched",
    );
  },

  /**
   * 読み取り専用モードを設定する
   */
  setReadOnly: (readOnly) => {
    set({ readOnly }, false, "core/setReadOnly");
  },

  /**
   * 初期値を設定する
   */
  setInitialValues: (initialValues: Record<FieldId, FieldValueType>) => {
    const state = get();
    if (state.initialValueProvider) {
      state.initialValueProvider.updateInitialValues(initialValues);
    }
  },

  /**
   * 初期値プロバイダーを設定する
   */
  setInitialValueProvider: (provider) => {
    set(
      { initialValueProvider: provider },
      false,
      "core/setInitialValueProvider",
    );
  },

  /**
   * 現在の初期値を取得する（プロバイダー経由）
   */
  getInitialValues: () => {
    const state = get();
    return state.initialValueProvider?.getInitialValues() || {};
  },
});

function computeNewValues(
  currentValues: FieldValue[],
  fieldId: FieldId,
  value: FieldValueType,
  attribute: Attribute | undefined,
): FieldValue[] {
  let newValues = [...currentValues];
  const exists = newValues.some((v) => v.fieldId === fieldId);
  if (exists) {
    newValues = newValues.map((v) =>
      v.fieldId === fieldId ? { ...v, value } : v,
    );
  } else {
    if (!attribute) return newValues;
    newValues.push({ fieldId, attributeId: attribute.id, value });
  }
  return newValues;
}
